import React from "react";
import { formatBandHeader } from "./matrixAdapters";

export const METADATA_COLUMN_IDS = {
  selection: "selection",
  locality: "meta:locality",
  kato: "meta:kato",
  address: "meta:address",
  isActive: "meta:isActive",
};
const METADATA_COLUMN_ID_SET = new Set(Object.values(METADATA_COLUMN_IDS));

const EMPTY_VALUE = "\u2014";
const READONLY_CELL_CLASS = "cross-docking-sheet__cell cross-docking-sheet__cell--readonly";
const TARIFF_CELL_CLASS = "cross-docking-sheet__cell cross-docking-sheet__cell--tariff";
const DIRTY_CELL_CLASS = "cross-docking-sheet__cell--dirty";
const SELECTION_CELL_CLASS = "cross-docking-sheet__cell cross-docking-sheet__cell--selection";
const ACTIVE_TOGGLE_CELL_CLASS = "cross-docking-sheet__cell cross-docking-sheet__cell--active-toggle";
const DEFAULT_METADATA_COLUMNS = [
  { columnId: METADATA_COLUMN_IDS.selection, width: 44, resizable: false },
  { columnId: METADATA_COLUMN_IDS.locality, width: 160, resizable: true },
  { columnId: METADATA_COLUMN_IDS.kato, width: 130, resizable: true },
  { columnId: METADATA_COLUMN_IDS.address, width: 260, resizable: true },
  { columnId: METADATA_COLUMN_IDS.isActive, width: 140, resizable: true },
];

export const isMetadataColumnId = (columnId) => METADATA_COLUMN_ID_SET.has(columnId);
export const isTariffColumnId = (columnId) => !isMetadataColumnId(columnId);

export const normalizeTariffColumnOrder = (currentOrder = [], nextBandColumnIds = []) => {
  const knownIds = new Set(nextBandColumnIds);
  const preserved = currentOrder.filter((columnId) => knownIds.has(columnId));
  const missing = nextBandColumnIds.filter((columnId) => !preserved.includes(columnId));

  return [...preserved, ...missing];
};

export const reorderTariffColumnIds = (currentOrder = [], movedColumnIds = [], targetColumnId, dropPosition) => {
  const movingSet = new Set(movedColumnIds);
  const remaining = currentOrder.filter((columnId) => !movingSet.has(columnId));
  const targetIndex = remaining.indexOf(targetColumnId);

  if (targetIndex === -1) {
    return currentOrder;
  }

  const insertionIndex = dropPosition === "after" ? targetIndex + 1 : targetIndex;
  return [
    ...remaining.slice(0, insertionIndex),
    ...movedColumnIds,
    ...remaining.slice(insertionIndex),
  ];
};

const buildHeaderCell = (text) => ({
  type: "header",
  text,
  className: "cross-docking-sheet__cell cross-docking-sheet__cell--header",
});

const buildReadonlyTextCell = (text, className = READONLY_CELL_CLASS) => ({
  type: "text",
  text,
  nonEditable: true,
  className,
});

export const buildSelectionHeaderCell = ({ allSelected = false, someSelected = false, onToggleAll } = {}) => ({
  type: "selectionHeader",
  checked: Boolean(allSelected),
  indeterminate: Boolean(!allSelected && someSelected),
  ariaLabel: "Выбрать все строки",
  onToggleAll: typeof onToggleAll === "function" ? onToggleAll : undefined,
  nonEditable: true,
  className: SELECTION_CELL_CLASS,
});

export const buildSelectionRowCell = ({ rowId, selected = false, onToggle } = {}) => ({
  type: "checkbox",
  checked: Boolean(selected),
  rowId,
  onToggle: typeof onToggle === "function" ? onToggle : undefined,
  ariaLabel: "Выбрать строку",
  className: SELECTION_CELL_CLASS,
});

export const buildActiveToggleCell = ({ row, onToggle } = {}) => ({
  type: "activeToggle",
  isActive: Boolean(row?.isActive),
  rowId: row?.crossDockingPointId,
  onToggle: typeof onToggle === "function" ? onToggle : undefined,
  nonEditable: true,
  className: ACTIVE_TOGGLE_CELL_CLASS,
});

const activeToggleCellTemplate = {
  getCompatibleCell(uncertainCell) {
    return {
      ...uncertainCell,
      type: "activeToggle",
      isActive: Boolean(uncertainCell?.isActive),
      text: uncertainCell?.isActive ? "да" : "нет",
      value: uncertainCell?.isActive ? 1 : 0,
    };
  },
  isFocusable() {
    return true;
  },
  render(cell) {
    return React.createElement(
      "button",
      {
        type: "button",
        "aria-label": "Переключить активность",
        "aria-pressed": Boolean(cell?.isActive),
        className: "cross-docking-sheet__active-toggle",
        onPointerDown: (event) => {
          event.stopPropagation();
        },
        onClick: (event) => {
          event.stopPropagation();
          if (typeof cell?.onToggle === "function") {
            cell.onToggle();
          }
        },
      },
      cell?.isActive ? "да" : "нет",
    );
  },
};

const selectionHeaderCellTemplate = {
  getCompatibleCell(uncertainCell) {
    return {
      ...uncertainCell,
      type: "selectionHeader",
      checked: Boolean(uncertainCell?.checked),
      indeterminate: Boolean(uncertainCell?.indeterminate),
      text: uncertainCell?.checked ? "all" : "",
      value: uncertainCell?.checked ? 1 : 0,
    };
  },
  isFocusable() {
    return true;
  },
  render(cell) {
    return React.createElement("input", {
      type: "checkbox",
      "aria-label": cell?.ariaLabel || "Выбрать все строки",
      checked: Boolean(cell?.checked),
      ref: (node) => {
        if (node) {
          node.indeterminate = Boolean(cell?.indeterminate);
        }
      },
      onPointerDown: (event) => {
        event.stopPropagation();
      },
      onChange: (event) => {
        event.stopPropagation();
        if (typeof cell?.onToggleAll === "function") {
          cell.onToggleAll();
        }
      },
    });
  },
};

const checkboxCellTemplate = {
  getCompatibleCell(uncertainCell) {
    return {
      ...uncertainCell,
      type: "checkbox",
      checked: Boolean(uncertainCell?.checked),
      text: uncertainCell?.checked ? "on" : "",
      value: uncertainCell?.checked ? 1 : 0,
    };
  },
  isFocusable() {
    return true;
  },
  render(cell, isInEditMode, onCellChanged) {
    return React.createElement("input", {
      type: "checkbox",
      "aria-label": cell?.ariaLabel || "Выбрать строку",
      checked: Boolean(cell?.checked),
      className: cell?.className || "",
      onPointerDown: (event) => {
        event.stopPropagation();
      },
      onChange: (event) => {
        event.stopPropagation();
        const newChecked = event.target.checked;
        if (typeof cell?.onToggle === "function") {
          cell.onToggle();
        }
        if (typeof onCellChanged === "function") {
          onCellChanged({ ...cell, checked: newChecked }, true);
        }
      },
    });
  },
};

export const customCellTemplates = {
  activeToggle: activeToggleCellTemplate,
  selectionHeader: selectionHeaderCellTemplate,
  checkbox: checkboxCellTemplate,
};

export { activeToggleCellTemplate, selectionHeaderCellTemplate };

const isDirtyCell = (cell = {}) =>
  cell?.draftPrice != null && String(cell.draftPrice) !== String(cell.price);

export const getReactGridCellValue = (cell = {}) => {
  if (cell.type === "number") {
    return Number.isFinite(cell.value) ? String(cell.value) : "";
  }

  return cell?.text || "";
};

const buildTariffNumberCell = (cell = {}) => {
  const rawValue = cell?.draftPrice ?? cell?.price;
  const numericValue = rawValue === "" || rawValue == null ? Number.NaN : Number(rawValue);
  const className = isDirtyCell(cell)
    ? `${TARIFF_CELL_CLASS} ${DIRTY_CELL_CLASS}`
    : TARIFF_CELL_CLASS;

  return {
    type: "number",
    value: Number.isFinite(numericValue) ? numericValue : Number.NaN,
    className,
  };
};

export const buildTariffGridColumns = (bands = [], options = {}) => {
  const {
    tariffColumnOrder,
    columnWidthsById = {},
  } = options;
  const orderedBandColumnIds = normalizeTariffColumnOrder(
    tariffColumnOrder || bands.map((band) => band.columnKey),
    bands.map((band) => band.columnKey)
  );

  return [
    ...DEFAULT_METADATA_COLUMNS,
    ...orderedBandColumnIds.map((columnId) => ({
      columnId,
      width: columnWidthsById[columnId] || 130,
      resizable: true,
      reorderable: true,
    })),
  ];
};

const orderBands = (bands = [], tariffColumnOrder = []) => {
  const bandsById = new Map(bands.map((band) => [band.columnKey, band]));
  return normalizeTariffColumnOrder(tariffColumnOrder, bands.map((band) => band.columnKey))
    .map((columnId) => bandsById.get(columnId))
    .filter(Boolean);
};

export const buildTariffGridRows = ({
  rows = [],
  bands = [],
  tariffColumnOrder = [],
  selectedRowIds,
  onRowSelectionToggle,
  onHeaderSelectionToggle,
  onToggleActive,
}) => {
  const orderedBands = orderBands(bands, tariffColumnOrder);
  const selectionSet = selectedRowIds instanceof Set
    ? selectedRowIds
    : new Set(Array.isArray(selectedRowIds) ? selectedRowIds : []);
  const totalRows = rows.length;
  let selectedCount = 0;
  for (const row of rows) {
    if (selectionSet.has(row?.crossDockingPointId)) {
      selectedCount += 1;
    }
  }
  const allSelected = totalRows > 0 && selectedCount === totalRows;
  const someSelected = selectedCount > 0 && selectedCount < totalRows;

  const headerRow = {
    rowId: "header",
    height: 40,
    cells: [
      buildSelectionHeaderCell({
        allSelected,
        someSelected,
        onToggleAll: typeof onHeaderSelectionToggle === "function" ? onHeaderSelectionToggle : undefined,
      }),
      buildHeaderCell("Город КД"),
      buildHeaderCell("КАТО"),
      buildHeaderCell("Адрес склада"),
      buildHeaderCell("Признак кросс докинга да/нет"),
      ...orderedBands.map((band) => buildHeaderCell(formatBandHeader(band))),
    ],
  };

  const dataRows = rows.map((row) => ({
    rowId: `row:${row.crossDockingPointId}`,
    height: 36,
    cells: [
      buildSelectionRowCell({
        rowId: row?.crossDockingPointId,
        selected: selectionSet.has(row?.crossDockingPointId),
        onToggle:
          typeof onRowSelectionToggle === "function"
            ? () => onRowSelectionToggle(row, !selectionSet.has(row?.crossDockingPointId))
            : undefined,
      }),
      buildReadonlyTextCell(row?.locality || EMPTY_VALUE),
      buildReadonlyTextCell(row?.kato || EMPTY_VALUE),
      buildReadonlyTextCell(row?.address || EMPTY_VALUE),
      buildActiveToggleCell({
        row,
        onToggle:
          typeof onToggleActive === "function"
            ? () => onToggleActive(row, !row?.isActive)
            : undefined,
      }),
      ...orderedBands.map((band) => buildTariffNumberCell(row?.cells?.[band.columnKey] || {})),
    ],
  }));

  return [headerRow, ...dataRows];
};

export const translateReactGridChanges = ({
  changes = [],
  rowLookupById,
  onPriceChange,
}) => {
  changes.forEach((change) => {
    if (String(change?.rowId || "").startsWith("row:") === false) {
      return;
    }

    if (isMetadataColumnId(change.columnId)) {
      return;
    }

    if (change.type !== "number") {
      return;
    }

    const row = rowLookupById.get(change.rowId);
    if (!row) {
      return;
    }

    onPriceChange(row, change.columnId, getReactGridCellValue(change.newCell));
  });
};
